import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type {
  ChecklistTemplateBody,
  EventChecklistItemAdd,
  EventChecklistItemPatch,
  EventChecklistResponse
} from '../types'

// Catalogue side (admin)

export function useChecklistTemplates() {
  return useQuery({
    queryKey: ['checklist-templates'],
    queryFn: ApiClient.checklistTemplates,
    // Templates rarely change; cache for 5 min so the per-event picker
    // opens instantly after the admin page is loaded once.
    staleTime: 5 * 60_000
  })
}

export function useChecklistTemplate(id: number) {
  return useQuery({
    queryKey: ['checklist-template', id],
    queryFn: () => ApiClient.checklistTemplate(id),
    enabled: Number.isFinite(id) && id > 0
  })
}

export function useCreateChecklistTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ChecklistTemplateBody) => ApiClient.createChecklistTemplate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklist-templates'] })
    }
  })
}

export function useUpdateChecklistTemplate(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ChecklistTemplateBody) => ApiClient.updateChecklistTemplate(id, body),
    onSuccess: (updated) => {
      qc.setQueryData(['checklist-template', id], updated)
      qc.invalidateQueries({ queryKey: ['checklist-templates'] })
    }
  })
}

export function useDeleteChecklistTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => ApiClient.deleteChecklistTemplate(id).then(() => id),
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['checklist-templates'] })
      qc.removeQueries({ queryKey: ['checklist-template', id] })
    }
  })
}

// Per-event side

export function useEventChecklist(eventId: number) {
  return useQuery({
    queryKey: ['event-checklist', eventId],
    queryFn: () => ApiClient.eventChecklist(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

export function useApplyChecklistTemplate(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (templateId: number) => ApiClient.applyChecklistTemplate(eventId, templateId),
    onSuccess: (resp: EventChecklistResponse) => {
      qc.setQueryData(['event-checklist', eventId], resp)
    }
  })
}

export function useAddChecklistItem(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EventChecklistItemAdd) => ApiClient.addChecklistItem(eventId, body),
    onSuccess: (resp: EventChecklistResponse) => {
      qc.setQueryData(['event-checklist', eventId], resp)
    }
  })
}

export function usePatchChecklistItem(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: number; body: EventChecklistItemPatch }) =>
      ApiClient.patchChecklistItem(eventId, itemId, body),
    onSuccess: (resp: EventChecklistResponse) => {
      qc.setQueryData(['event-checklist', eventId], resp)
    }
  })
}

export function useDeleteChecklistItem(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (itemId: number) =>
      ApiClient.deleteChecklistItem(eventId, itemId).then(() => itemId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event-checklist', eventId] })
    }
  })
}
